import { TrashIcon, WarningIcon } from '@phosphor-icons/react';
import {
  ChevronDown,
  ChevronRight,
  CircleCheckIcon,
  GripVerticalIcon,
} from 'lucide-react';
import {
  FieldArray,
  FieldValues,
  UseFieldArrayRemove,
  useFormContext,
} from 'react-hook-form';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import { Autocomplete } from '@/modules/common';
import { MultiSelect } from '@/modules/common/components/multi-select';
import { OptionType } from '@/modules/common/types';
import { positionOptions } from '@/modules/events/constants';
import { ModelSize } from '@/modules/model-sizes/types';

interface ModelColumnsProps {
  form: {
    update: (index: number, value: FieldArray<FieldValues, string>) => void;
    remove: UseFieldArrayRemove;
  };
  options: {
    modelOptions: OptionType[];
    sizeOptions: ModelSize[];
  };
}
export const useModelColumns = ({ form, options }: ModelColumnsProps) => {
  const { control } = useFormContext();

  return [
    {
      id: 'dragHandle',
      header: '',
      cell: () => (
        <GripVerticalIcon
          size={18}
          className="text-zinc-500"
          aria-hidden="true"
        />
      ),
    },
    {
      id: 'expander',
      cell: ({ row }) => (
        <Button
          type="button"
          variant="transparent"
          size="sm"
          className="p-0"
          onClick={(e) => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
        >
          {row.getIsExpanded() ? <ChevronDown /> : <ChevronRight />}
        </Button>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Model Type',
      cell: ({ row }) => (
        <Autocomplete
          name={`models.${row.index}.modelId`}
          control={control}
          searchable
          placeholder="Select model type"
          options={options.modelOptions}
          className="w-[210px] border-0 p-0 mt-0 h-auto"
        />
      ),
    },
    {
      accessorKey: 'sizes',
      header: 'Available Size(s)',
      cell: ({ row }) => {
        const sizes = row.getValue('sizes');
        const value = sizes.map((size) => size.value);

        return (
          <span className="line-clamp-1 w-[140px]">{value.join(', ')}</span>
        );
      },
    },
    {
      accessorKey: 'quantity',
      header: 'Quantity',
      cell: ({ row }) => {
        const sizes = row.getValue('sizes');
        const value = sizes.reduce(
          (accumulator, current) => accumulator + current.quantity,
          0
        );

        return value;
      },
    },
    {
      accessorKey: 'reservedQuantity',
      header: 'Reserved',
      cell: ({ row }) => {
        const sizes = row.getValue('sizes');
        const value = sizes.reduce(
          (accumulator, current) => accumulator + current.reservedQuantity,
          0
        );

        return value;
      },
    },
    {
      accessorKey: 'position',
      header: 'Image Position',
      cell: ({ row }) => {
        const selectedModelId = row.original?.modelId;

        const selectedModelOption = options.modelOptions?.find(
          (option) => option.id === selectedModelId
        );
        const availablePositions = positionOptions.filter((option) =>
          selectedModelOption?.sides.includes(option.value)
        );

        return (
          <MultiSelect
            name={`models.${row.index}.position`}
            control={control}
            placeholder="Select position"
            className="[&_button]:border-0 w-[340px]"
            selectClassName="p-0"
            options={availablePositions}
            maxCount={3}
          />
        );
      },
    },
    {
      size: 190,
      accessorKey: 'gcrFile',
      header: 'GCR File',
      cell: ({ row }) => {
        const { modelId, sizes, gcrFiles } = row.original;

        if (!modelId || !sizes) return '';

        switch (gcrFiles.action) {
          case 'redirect':
            return (
              <p className="text-destructive normal-case">
                <WarningIcon size={24} className="inline mr-1" /> Please add or
                enable printers for this model
              </p>
            );
          case 'create':
            return (
              <p className="text-destructive normal-case">
                <WarningIcon size={24} className="inline mr-1" /> No GCR file
                for {gcrFiles.printers.length}{' '}
                {gcrFiles.printers.length === 1 ? 'size' : 'sizes'}
              </p>
            );
          case 'none':
            return <CircleCheckIcon color={colors.green[400]} size={20} />;
          case 'notNeeded':
            return (
              <span className="normal-case text-zinc-400">Not Needed</span>
            );
          default:
            return '';
        }
      },
    },
    {
      id: 'actions',
      header: 'Action',
      enableHiding: false,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => form.remove(row.index)}
          data-testid="remove-model-button"
        >
          <TrashIcon size={20} color={colors.zinc[400]} />
        </Button>
      ),
    },
  ];
};
